import React, { createContext, useContext, useMemo } from 'react';
import { useAppSelector } from '@/stores';
import { selectUser } from '@/stores/selectors';
import { createAnalyticsClient, type AnalyticsClient } from './client';

const AnalyticsContext = createContext<AnalyticsClient | null>(null);
export const __AnalyticsContextForEnricher = AnalyticsContext;

export interface AnalyticsProviderProps {
    children: React.ReactNode;
    /** Override for tests — inject a mock client. */
    client?: AnalyticsClient;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children, client }) => {
    const value = useMemo(() => client ?? createAnalyticsClient(), [client]);
    return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
};

/**
 * Hook to access the analytics client. Returns a no-op-safe client even
 * outside the provider, so call sites never need to null-check.
 */
export const useAnalytics = (): AnalyticsClient => {
    const ctx = useContext(AnalyticsContext);
    if (ctx) return ctx;
    return noopClient;
};

const noopClient: AnalyticsClient = {
    track: () => undefined,
    identify: () => undefined,
    groupAccount: () => undefined,
    groupWorkspace: () => undefined,
    page: () => undefined,
    reset: () => undefined,
    isEnabled: () => false,
};

/**
 * Wraps the base analytics client to stamp account_id / subdomain / role onto
 * every track payload. Lives inside the Redux Provider (the base
 * AnalyticsProvider is mounted outside it), and re-provides the same context
 * so all consumers transparently get the enriched client.
 *
 * Why on `properties` and not just on group/identify traits: it lets analysts
 * answer "unique accounts that did X" with a single COUNT(DISTINCT account_id)
 * without depending on Segment's group-join semantics in the warehouse.
 *
 * `account_id` is mapped to `subdomain` because this app's user model has no
 * dedicated customer/account identifier; subdomain is the stable per-account
 * key on PagerDuty.
 */
export const AnalyticsEnricher: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const base = useContext(AnalyticsContext);
    const user = useAppSelector(selectUser);

    const enriched = useMemo<AnalyticsClient>(() => {
        const inner = base ?? noopClient;
        const contextProps: Record<string, unknown> = {};
        if (user?.subdomain) {
            contextProps.account_id = user.subdomain;
            contextProps.subdomain = user.subdomain;
        }
        if (user?.role) contextProps.role = user.role;

        return {
            ...inner,
            track: (payload) => {
                inner.track({
                    name: payload.name,
                    properties: { ...contextProps, ...(payload.properties as Record<string, unknown>) },
                });
            },
        };
    }, [base, user]);

    return <AnalyticsContext.Provider value={enriched}>{children}</AnalyticsContext.Provider>;
};
